import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useRef, useState, useMemo, useId, useEffect } from "react";
import { Navigation, Scrollbar, A11y, Autoplay, Keyboard, } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
// components
import { Indicator, LoopIndicator } from "../indicator";
// hooks
import { useIsMobile } from "./use-is-mobile";
import { useMedia } from "../../utils/use-media";
import { makeDataTestIdGetter } from "../../utils";
// styles
import { StyledButton, CarouselContainer } from "./carousel.styles";
// constants
import { DEFAULT_RESPONSIVE_LAYOUT, DEFAULT_SLIDE_MARGIN, INITIAL_SLIDES_CONFIG, breakpoints, initSlides, } from "./constants";
export const Carousel = (props) => {
    const { isMobile, isTablet } = useIsMobile();
    return _jsx(CarouselView, Object.assign({}, props, { isMobile: isMobile, isTablet: isTablet }));
};
export const CarouselView = ({ children, responsiveLayout = DEFAULT_RESPONSIVE_LAYOUT, onChange, hideButtons = false, hideButtonsTablet = false, slidesWidth = [], slideMargin = DEFAULT_SLIDE_MARGIN, customButtonLeft, customButtonRight, previewId, startIndex, previewIdExtractor, autoSliding = false, loop = false, dark = false, slidingInterval = 4, hideIndicator = false, prevEl, nextEl, isMobile, isTablet, id, dataTestIdPrefix, slideToIndex, }) => {
    const [initStepsToMove, slidesToShow] = useMedia(breakpoints, initSlides(Object.assign(Object.assign({}, DEFAULT_RESPONSIVE_LAYOUT), responsiveLayout)), INITIAL_SLIDES_CONFIG);
    const isClient = typeof window !== "undefined";
    const uniqueId = useId().replace(/[^a-zA-Z0-9]/g, "");
    const [activeSlide, setActiveSlide] = useState(startIndex);
    const classes = useRef({
        left: id ? `${id}-left` : `button-${uniqueId}-left`,
        right: id ? `${id}-right` : `button-${uniqueId}-right`,
        thumb: `thumb-${uniqueId}`,
        track: `track-${uniqueId}`,
    });
    const swiperRef = useRef();
    useEffect(() => {
        var _a;
        if (slideToIndex >= 0) {
            (_a = swiperRef.current) === null || _a === void 0 ? void 0 : _a.slideTo(slideToIndex);
        }
    }, [slideToIndex]);
    const shouldRenderControlButtons = useMemo(() => (!isMobile && !isTablet) ||
        (isMobile && !hideButtons) ||
        (isTablet && !hideButtonsTablet), [isMobile, isTablet, hideButtons, hideButtonsTablet]);
    const slidesCount = React.Children.count(children);
    const dragSize = (slidesToShow / slidesCount) * 100;
    const getDataTestId = makeDataTestIdGetter(dataTestIdPrefix);
    const renderIndicator = () => {
        if (loop) {
            return (_jsx(LoopIndicator, { show: !hideIndicator, slidesCount: slidesCount, swiper: swiperRef.current, indicatorWidth: (1 / slidesCount) * 100, activeSlide: activeSlide || 0, dark: dark }));
        }
        return (_jsx(Indicator, { show: !hideIndicator, classes: classes.current, dark: dark }));
    };
    return (_jsxs("div", Object.assign({ "data-preview-id": previewId }, { children: [_jsxs(CarouselContainer, Object.assign({ className: "carousel-container", slideMargin: slideMargin, 
                // used only in ssr mode
                style: {
                    "--carousel-width-small": 100 / responsiveLayout.small.slides + "%",
                    "--carousel-width-medium": 100 / responsiveLayout.medium.slides + "%",
                    "--carousel-width-large": 100 / responsiveLayout.large.slides + "%",
                } }, { children: [_jsx(StyledButton, { show: shouldRenderControlButtons && !customButtonLeft && !prevEl, className: `gdds-carousel__left-button ${classes.current.left}`, buttonType: "button", isIcon: true, symbol: "chevron_left", stylingType: dark ? "flatInverted" : "flat", "data-testid": getDataTestId("carousel-button-left") }), _jsx(Swiper
                    // used to set the correct slides width in SSR mode (no hydration)
                    // this is because useMedia hook is not executed in SSR
                    // and therefore we don't have the correct slidesToShow value in the corresponding viewport
                    , Object.assign({ 
                        // used to set the correct slides width in SSR mode (no hydration)
                        // this is because useMedia hook is not executed in SSR
                        // and therefore we don't have the correct slidesToShow value in the corresponding viewport
                        className: isClient ? "" : "ssr-mode", modules: [A11y, Scrollbar, Navigation, Autoplay, Keyboard], onSwiper: (swiper) => {
                            window.swiper = swiper;
                            swiperRef.current = swiper;
                        }, touchRatio: initStepsToMove, observer: true, scrollbar: {
                            el: `.${classes.current.track}`,
                            draggable: true,
                            snapOnRelease: true,
                            dragClass: classes.current.thumb,
                            dragSize,
                        }, autoplay: autoSliding && {
                            delay: slidingInterval * 1000,
                            pauseOnMouseEnter: true,
                            disableOnInteraction: false,
                        }, keyboard: {
                            enabled: true,
                            onlyInViewport: true,
                        }, a11y: {
                            prevSlideMessage: "Previous slide",
                            nextSlideMessage: "Next slide",
                        }, onSlideChange: (s) => {
                            if (onChange)
                                onChange(s.realIndex);
                            setActiveSlide(s.realIndex);
                        }, slidesPerView: slidesWidth.length > 0 ? "auto" : slidesToShow, spaceBetween: slideMargin, initialSlide: startIndex, speed: 500, loop: isClient && loop, navigation: {
                            nextEl: nextEl || `.${classes.current.right}`,
                            prevEl: prevEl || `.${classes.current.left}`,
                        } }, { children: Array.isArray(children) ? (children.map((slide, index) => (_jsx(SwiperSlide, Object.assign({ "data-preview-id": typeof previewIdExtractor === "function"
                                ? previewIdExtractor(slide, index)
                                : undefined, style: slidesWidth[index]
                                ? { width: `${String(slidesWidth[index])}%` }
                                : {} }, { children: slide }), `slide-${String(index)}`)))) : (_jsx(SwiperSlide, Object.assign({ style: { width: "100%" } }, { children: children }))) })), _jsx(StyledButton, { className: `gdds-carousel__right-button ${classes.current.right}`, buttonType: "button", show: shouldRenderControlButtons && !customButtonRight && !nextEl, isIcon: true, symbol: "chevron_right", stylingType: dark ? "flatInverted" : "flat", "data-testid": getDataTestId("carousel-button-right") })] })), renderIndicator()] })));
};
